import {
  useTranslations,
  ReactHookFormAutocomplete,
  useHeroSnackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  DialogActions,
  Button,
  S600,
  Typography,
  ScrollableBox,
} from '@uniqkey-frontend/shared-app';
import { memo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  GetPartnersForLookupResponseModel,
  PartnerType,
  PartnerMoveRequest,
} from '@uniqkey-backend-partner/api-client';
import usePartnersAPI from '../../../../hooks/usePartnersAPI';
import { IPartnersTableRow } from '../../../../hooks/tables/usePartnersTable';

export interface IMovePartnersModalSubmitResult extends Pick<
  PartnerMoveRequest, 'targetPartnerId'
> {}

interface IMovePartnersModalFormValue {
  targetPartner: TTargetPartnerOption;
}

type TTargetPartnerOption = {
  label: string;
  value: string;
} | null;

interface IMovePartnersModalProps {
  open: boolean;
  onClose: () => void;
  isLoading: boolean;
  onSubmit: (params: IMovePartnersModalSubmitResult) => Promise<void> | void;
  selectedPartnersAsObjects: IPartnersTableRow[];
  selectedPartnersType: typeof PartnerType.Distributor | typeof PartnerType.Sales;
}

const PARTNER_TYPES_FOR_LOOKUP = {
  [PartnerType.Distributor]: [PartnerType.Ambassador],
  [PartnerType.Sales]: [PartnerType.Distributor, PartnerType.Ambassador],
};

const SCROLLABLE_BOX_SX = { height: '100%', maxHeight: 190 };

const MovePartnersModal = (props: IMovePartnersModalProps) => {
  const {
    open,
    onClose,
    selectedPartnersAsObjects,
    selectedPartnersType,
    isLoading,
    onSubmit,
  } = props;
  const { t } = useTranslations();
  const { showError } = useHeroSnackbar();
  const { getPartnersForLookup } = usePartnersAPI();

  const {
    handleSubmit, control, formState: { isDirty },
  } = useForm<IMovePartnersModalFormValue>({
    defaultValues: {
      targetPartner: null,
    },
  });

  const handleGetPartnersForLookupRequest = useCallback(async (searchQuery: string) => {
    try {
      const { data } = await getPartnersForLookup({
        page: 1,
        pageLength: 100,
        partnerTypes: PARTNER_TYPES_FOR_LOOKUP[selectedPartnersType],
        searchQuery,
      });
      return data;
    } catch (e) {
      showError({ text: t('common.somethingWentWrong') });
      return [];
    }
  }, [getPartnersForLookup, selectedPartnersType, showError, t]);

  const handleGetPartnersForLookupResponseParser = useCallback((
    partners: GetPartnersForLookupResponseModel[],
  ) => partners.map(
    (organization) => ({
      value: organization.partnerId,
      label: organization.partnerName,
    }),
  ), []);

  const handleFormSubmit = useCallback((formValue: IMovePartnersModalFormValue): void => {
    const { targetPartner } = formValue;
    onSubmit({
      targetPartnerId: targetPartner!.value,
    });
  }, [onSubmit]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      clickOutsideToClose={!isLoading}
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} autoComplete="off" noValidate>
        <DialogTitle isLoading={isLoading} onClose={onClose}>
          {t('movePartnersModal.title', { count: selectedPartnersAsObjects.length })}
        </DialogTitle>
        <DialogContent>
          <Grid container flexDirection="column" rowGap={2}>
            <Typography>
              {t('movePartnersModal.description')}
            </Typography>
            <ScrollableBox sx={SCROLLABLE_BOX_SX}>
              {selectedPartnersAsObjects.map((partner) => (
                <Typography noWrap color={S600} key={partner.partnerId}>
                  {partner.name}
                </Typography>
              ))}
            </ScrollableBox>
            <Typography>
              {t('movePartnersModal.additionalDescription')}
            </Typography>
            <ReactHookFormAutocomplete
              name="targetPartner"
              control={control}
              t={t}
              placeholder={t('movePartnersModal.targetPartner.placeholder')}
              dataSourceRequest={handleGetPartnersForLookupRequest}
              dataSourceResponseParser={handleGetPartnersForLookupResponseParser}
              fetchOnEmptyQuery
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button
                type="submit"
                disabled={!isDirty}
                isLoading={isLoading}
                fullWidth
              >
                {t('common.move')}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button onClick={onClose} disabled={isLoading} variant="outlined" fullWidth>
                {t('common.cancel')}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default memo(MovePartnersModal);
